import { Project } from '@material-bank/projects-api';
import { getUserLang } from '@mb/lib';
import { handleSystemErrorReport } from '@mb/lib/utils';
import { LocalStorageCartItem } from '@mb/types';
import { getCartItems } from '@productSearch//utils/localstorage';
import { extraFieldsList } from '@productSearch/hooks';
import { getBGFilters } from '@productSearch/hooks/searcServiseUtils';
import { ApiRawMaterial } from '@productSearch/types';
import axios from 'axios';
import indexOf from 'lodash/indexOf';
import sortBy from 'lodash/sortBy';
import { siteId, endpoints, rawApiURL } from '../config';
import {
  checkStockAvailability,
  getUserAttributes,
  transformMaterialToAppFormat,
} from '../helpers';

const cartItems = getCartItems();
type RawCookieObjectType = {
  [key: string]: string;
};
type CurrentProdjectCookieType = {
  project_id: number;
  type: string;
  title: string;
  type_name: string;
  location_state: string;
};
const localExtraFields = [
  ...extraFieldsList,
  'size_notes',
  'content_textile',
  'price_range_verbose',
  'pricing_unit_of_measure',
  'billing_type',
  'sample_note',
];

export const checkMultipleMaterialsAvailability = async (
  materialsIds: string[],
) => {
  try {
    if (materialsIds?.length === 0) {
      return null;
    }

    const bodyFormData = new FormData();
    bodyFormData.append('items', materialsIds?.join(','));
    bodyFormData.append('origin', 'search');

    const response = await axios.post(
      '/catalog/product/inventory',
      bodyFormData,
    );
    const { data } = response;
    return data?.data;
  } catch (error) {
    console.error('Error while checking stock >>> ', error.message);
    handleSystemErrorReport(`Error while checking stock: ${error.message}`);
    return null;
  }
};

export const getMaterialsByKey = async (
  keyList: (string | number)[],
  isParent = false,
  currentProject: Project | null = null,
) => {
  try {
    const customer = getUserAttributes();
    const storageLang = getUserLang();
    const configLang =
      typeof ENVIRONMENT_REGION !== 'undefined' ? ENVIRONMENT_REGION : null;
    const body: any = {
      siteId: siteId,
      'key.entity_id': keyList,
      fieldsOnly: true,
      projectType: customer?.current_project_type,
      extraFields: localExtraFields,
    };
    const bgFilters = getBGFilters();

    body['segmentation.member.customer_group_name'] = [
      `${customer?.groupName || ''}`,
    ];
    body['segmentation.member.member_type_name'] = [
      `${customer?.member_type || ''}`,
    ];
    body['segmentation.member.primary_area_spec'] = [
      `${customer?.area_spec || ''}`,
    ];
    body['segmentation.member.job_type'] = [`${customer?.job_type}`];
    body['segmentation.member.is_test_user'] = [`${customer?.isTestUser}`];
    body['segmentation.member.country_id'] = [`${customer?.country}`];
    body['segmentation.member.state'] = [`${customer?.state}`];

    body['segmentation.project.project_type'] = [
      currentProject?.type?.label || '',
    ];
    body['segmentation.project.project_type_ref'] = [
      currentProject?.type?.externalId || body.projectType || '',
    ];
    body['segmentation.project.phase_name'] = [
      currentProject?.phase?.label ||
        `${customer?.current_project_phase_name || ''}`,
    ];
    body['segmentation.project.state'] = [currentProject?.state?.name || ''];
    body['segmentation.project.country'] = [
      currentProject?.country?.name || '',
    ];

    if (customer?.email != null) {
      body['customerEmail'] = customer?.email;
    }
    if (customer?.customer_id != null) {
      body['customerId'] = Number(customer?.customer_id) || null;
    }

    if (configLang != null) {
      body['lang'] = `${storageLang}-${ENVIRONMENT_REGION}`;
    }
    if (isParent) {
      body['recommendationStripLocations'] = ['minipdp'];
    }
    let xRequestId = undefined;

    const response = await fetch(endpoints.byKey, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...body, ...bgFilters }),
    }).then((res) => {
      xRequestId = res?.headers?.get('x-request-id');
      return res.json();
    });

    const { results = [], recommendationStrips, ...rest } = response;
    const sortedResults = sortBy(results, function (obj) {
      return indexOf(keyList, String(obj.entity_id));
    });
    return {
      results: sortedResults,
      recommendationStrips: recommendationStrips?.recommendationStrips || [],
      xRequestId,
      ...rest,
    };
  } catch (error) {
    handleSystemErrorReport(`Error while checking stock: ${error.message}`);
    console.error('Error getMaterialsByKey:', error);
    return null;
  }
};

export interface A1Props {
  urlPath: string;
  payload: any;
}

export const getRecommendStrip = async (props: A1Props) => {
  try {
    let xRequestId: string | null = null;
    const cartItemIds = cartItems?.map(
      (item: LocalStorageCartItem) => item.product_id,
    );
    const bgFilters = getBGFilters();
    const response = await fetch(`${rawApiURL}${props.urlPath}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...props.payload,
        extraFields: localExtraFields,
        excludeItemIds: cartItemIds || [],
        referrerUrl: window.location.host + window.location.pathname,
        ...bgFilters,
      }),
    }).then((res) => {
      xRequestId = res.headers.get('x-request-id');
      return res.json();
    });

    const results = response?.[0]?.results;
    // Add specific field for Split analytics
    const isComplementary =
      props.payload?.recommenderMode?.includes('complementary');
    const qvLocation = isComplementary ? 'complementary' : 'sampled';
    const analyticsPostfix = isComplementary ? 'compprods' : 'alsosampled';
    const ids = results?.map((item: ApiRawMaterial) => String(item.entity_id));
    const analyticsClass = 'recommend';

    if (Array.isArray(results)) {
      const materials = results.map((item) => {
        const newItem = transformMaterialToAppFormat(item);
        return {
          ...newItem,
          originRequestId: xRequestId || '',
          qvLocation,
          analyticsPostfix,
          siblingIds: ids,
          analyticsClass,
        };
      });
      if (materials.length === 0) {
        return [];
      }
      const stockResults = await checkStockAvailability(materials);
      return stockResults;
    }
    return [];
  } catch (error) {
    handleSystemErrorReport(
      `Error while getRecommendStripA1 miniPDP: ${error.message}`,
    );
    return [];
  }
};
